import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '@/services/api';

export type LanguageCode = 'it' | 'en' | 'es' | 'fr' | 'de' | 'pt';

export interface Language {
  code: LanguageCode;
  name: string;
  native: string;
  flag: string;
}

interface I18nState {
  language: LanguageCode;
  translations: Record<string, string>;
  languages: Language[];
  isLoading: boolean;
  setLanguage: (lang: LanguageCode) => void;
  loadTranslations: (lang: LanguageCode) => Promise<void>;
  loadLanguages: () => Promise<void>;
  t: (key: string, params?: Record<string, string>) => string;
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set, get) => ({
      language: 'it' as LanguageCode,
      translations: {},
      languages: [
        { code: 'it', name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
        { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
        { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
        { code: 'pt', name: 'Portuguese', native: 'Português', flag: '🇵🇹' },
      ],
      isLoading: false,

      setLanguage: (lang: LanguageCode) => {
        set({ language: lang });
        document.documentElement.lang = lang;
        get().loadTranslations(lang);
      },

      loadTranslations: async (lang: LanguageCode) => {
        set({ isLoading: true });
        try {
          const res = await api.get(`/i18n/translations/${lang}`);
          set({
            translations: res.data.translations || {},
            isLoading: false,
          });
        } catch {
          // Fallback: keep existing translations
          set({ isLoading: false });
        }
      },

      loadLanguages: async () => {
        try {
          const res = await api.get('/i18n/languages');
          if (res.data.languages) {
            set({ languages: res.data.languages });
          }
        } catch {
          // Keep defaults
        }
      },

      t: (key: string, params?: Record<string, string>) => {
        const { translations } = get();
        let text = translations[key] || key;
        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            text = text.replace(`{{${k}}}`, v);
          });
        }
        return text;
      },
    }),
    {
      name: 'rc-i18n',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        language: state.language,
        // Don't persist translations (reloaded on app start)
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          document.documentElement.lang = state.language;
          state.loadTranslations(state.language);
        }
      },
    },
  ),
);
